import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { PRAXIS, SPRECHZEITEN, LEISTUNGEN } from "../data/praxis";
import { api } from "../utils/api";

/**
 * Zentraler Store für dynamische Praxis-Inhalte.
 *  - `settings` (Kontakt-Daten wie name, phone, email, address, ...) — flach, camelCase
 *  - `hours`     (Sprechzeiten [{ day, time }, ...])
 *  - `services`  (Leistungen [{ slug, title, icon, short, bullets, note, ... }, ...])
 *
 * Beim Mount wird `/api/site-config.php` einmal abgeholt.
 * Bei Fehler/leerer Antwort greift der statische Fallback aus `praxis.js`.
 */

// Statischer Fallback im praxis.js-Format
const FALLBACK_SETTINGS = {
  name: PRAXIS.name,
  role: PRAXIS.role,
  street: PRAXIS.street,
  zip: PRAXIS.zip,
  city: PRAXIS.city,
  phone: PRAXIS.phone,
  phoneRaw: PRAXIS.phoneRaw,
  fax: PRAXIS.fax,
  faxRaw: PRAXIS.faxRaw,
  email: PRAXIS.email,
  web: PRAXIS.web,
  webUrl: PRAXIS.webUrl,
};

const FALLBACK_HOURS = SPRECHZEITEN.map((s, i) => ({
  id: -i - 1,
  day: s.day,
  time: s.time,
  sort_order: i,
}));

const FALLBACK_SERVICES = LEISTUNGEN.map((l, i) => ({
  id: -i - 1,
  slug: l.slug,
  title: l.title,
  icon: l.icon,
  short: l.short,
  bullets: l.bullets || [],
  note: l.note || "",
  has_page: !!l.hasPage,
  page_path: l.hasPage ? l.slug : "",
  is_new: !!l.isNew,
  new_label: l.newLabel || "",
  sort_order: i,
}));

// Fallback-Werte für Menopause + About (identisch mit den Original-Hardcodings)
const FALLBACK_MENOPAUSE = {
  hero_label: "Neu in der Praxis",
  hero_title: "Menopause & Wechseljahre",
  hero_em: "Videosprechstunde",
  hero_lead: "Das heutige Gesundheitssystem lässt für das so wichtige Thema Wechseljahre leider oft nicht die Zeit, die es verdient. Deshalb biete ich eine private Videosprechstunde an – damit ich mich ganz individuell und ohne Zeitdruck um Sie kümmern kann.",
  understand_label: "Hintergrund",
  understand_title: "Menopause <em>verstehen</em>\nBeschwerden erkennen und gezielt behandeln",
  understand_prose: "",
  ablauf_label: "Ablauf",
  ablauf_title: "In <em>4 einfachen</em> Schritten",
  ablauf_lead: "Von Ihrer Anfrage bis zum vertraulichen Gespräch – so läuft Ihre private Videosprechstunde ab.",
  steps: [],
  fineprint: "",
};

const FALLBACK_ABOUT = {
  label: "Über mich",
  name_pre: "Dr. med.",
  name_em: "Inka Scheer",
  intro: "",
  photo: "/team/dr-scheer.jpg",
  hero_photo: "/team/dr-scheer-freigestellt.png",
  badge_number: "20+",
  badge_text: "Jahre Erfahrung",
  career_heading: "Beruflicher Werdegang",
  career: [],
  awards_heading: "Auszeichnungen (Auswahl)",
  awards: [],
  memberships_heading: "Mitgliedschaften",
  memberships: [],
};

// snake_case (DB) → camelCase (Frontend) — nur für settings
function toCamelSettings(raw) {
  return {
    name: raw.name ?? "",
    role: raw.role ?? "",
    street: raw.street ?? "",
    zip: raw.zip ?? "",
    city: raw.city ?? "",
    phone: raw.phone ?? "",
    phoneRaw: raw.phone_raw ?? "",
    fax: raw.fax ?? "",
    faxRaw: raw.fax_raw ?? "",
    email: raw.email ?? "",
    web: raw.web ?? "",
    webUrl: raw.web_url ?? "",
  };
}

const PraxisContext = createContext({
  settings: FALLBACK_SETTINGS,
  hours: FALLBACK_HOURS,
  services: FALLBACK_SERVICES,
  menopause: FALLBACK_MENOPAUSE,
  about: FALLBACK_ABOUT,
  loading: true,
  refresh: () => {},
});

export function PraxisProvider({ children }) {
  const [state, setState] = useState({
    settings: FALLBACK_SETTINGS,
    hours: FALLBACK_HOURS,
    services: FALLBACK_SERVICES,
    menopause: FALLBACK_MENOPAUSE,
    about: FALLBACK_ABOUT,
    loading: true,
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.getSiteConfig();
      if (data && data.ok) {
        setState({
          settings: (data.settings && Object.keys(data.settings).length > 0)
            ? toCamelSettings(data.settings) : FALLBACK_SETTINGS,
          hours: (Array.isArray(data.hours) && data.hours.length > 0)
            ? data.hours : FALLBACK_HOURS,
          services: (Array.isArray(data.services) && data.services.length > 0)
            ? data.services : FALLBACK_SERVICES,
          menopause: (data.menopause && data.menopause.hero_title)
            ? { ...FALLBACK_MENOPAUSE, ...data.menopause } : FALLBACK_MENOPAUSE,
          about: (data.about && data.about.name_em)
            ? { ...FALLBACK_ABOUT, ...data.about } : FALLBACK_ABOUT,
          loading: false,
        });
        return;
      }
    } catch { /* fällt auf Fallback */ }
    setState((s) => ({ ...s, loading: false }));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <PraxisContext.Provider value={{ ...state, refresh }}>
      {children}
    </PraxisContext.Provider>
  );
}

export function usePraxis() {
  return useContext(PraxisContext);
}
